import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo, LanguageSwitcher } from "@/components/Brand";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", key: "home" },
    { to: "/gallery", key: "gallery" },
    { to: "/menu", key: "menu" },
    { to: "/hotels", key: "hotels" },
  ];
  // Displayed in the current UI language. The panel itself is Turkish-only,
  // but the entry-point label is translated for accessibility.
  const acenteLabel = { tr: "Acente Paneli", ar: "لوحة الوكالات", en: "Agency Panel", ru: "Панель агентств" }[lang] || "Acente Paneli";

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${
        scrolled
          ? "bg-canvas-cream/95 border-brand-navy/15 shadow-lg shadow-brand-navy/5"
          : "bg-canvas-cream/70 border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className={`max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between gap-4 transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <Link to="/" className="flex items-center" data-testid="nav-home-logo">
          <BrandLogo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  isActive
                    ? "text-brand-orange"
                    : "text-brand-navy/85 hover:text-brand-orange"
                }`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
          <NavLink
            to="/acente"
            data-testid="nav-link-acente"
            className={({ isActive }) =>
              `ml-1 px-4 py-2 text-sm font-medium rounded-full border transition-all ${
                isActive
                  ? "bg-brand-orange text-white border-brand-orange"
                  : "border-brand-orange/60 text-brand-orange hover:bg-brand-orange hover:text-white"
              }`
            }
          >
            {acenteLabel}
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <>
              <NavLink
                to="/admin"
                className="text-sm font-medium text-brand-navy hover:text-brand-orange"
                data-testid="nav-admin-dashboard"
              >
                {t("nav.admin")}
              </NavLink>
              <button
                onClick={logout}
                className="text-sm text-brand-navy/70 hover:text-brand-orange"
                data-testid="nav-logout-btn"
              >
                {t("cta.logout")}
              </button>
            </>
          ) : null}
        </div>

        <button
          className="lg:hidden p-2 rounded-full bg-brand-navy/5"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-navy/10 bg-canvas-cream" data-testid="nav-mobile-panel">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.key}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm ${isActive ? "bg-brand-navy text-white" : "text-brand-navy"}`
                }
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
            <NavLink
              to="/acente"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-acente"
              className={({ isActive }) =>
                `mt-1 px-3 py-2 rounded-lg text-sm border ${isActive ? "bg-brand-orange text-white border-brand-orange" : "text-brand-orange border-brand-orange/60"}`
              }
            >
              {acenteLabel}
            </NavLink>
            <div className="pt-3 flex items-center justify-between">
              <LanguageSwitcher />
              {user && (
                <button className="text-sm text-brand-navy" onClick={() => { logout(); setOpen(false); }}>
                  {t("cta.logout")}
                </button>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
